import { FC, useEffect, useRef, useState, HTMLProps } from 'react';
import styled from 'styled-components';

import { Tooltip } from 'ui/tooltip';
import { TooltipProps } from 'ui/tooltip/types';
import { fontWeights } from 'ui/theme';

import { Truncate, TruncateProps } from './Truncate';

const BaseLinkComponent = styled.a`
  font-weight: ${fontWeights.regular};
`;

export interface TruncateTooltipProps {
  link?: string;
  LinkComponent?: FC<React.PropsWithChildren<HTMLProps<HTMLAnchorElement>>>;
  maxWidth?: number;
  name: string;
  renderCustomContent?: TruncateProps['renderCustomContent'];
  textOnly?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  shouldLinkOpenInSameWindow?: boolean;
}

export const TruncateTooltip: FC<React.PropsWithChildren<TruncateTooltipProps>> = ({
  link,
  LinkComponent,
  maxWidth = 156,
  renderCustomContent,
  name,
  textOnly = false,
  tooltipProps = {},
  shouldLinkOpenInSameWindow = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const absoluteLink = `${window.location.origin}${link}`;

  const linkProps = shouldLinkOpenInSameWindow ? {} : { target: '_blank' };

  useEffect(() => {
    if (!ref.current) return;

    const truncated = ref.current.offsetWidth < ref.current.scrollWidth;
    setIsTruncated(truncated);
  }, [name, textOnly, link]);

  const Link = LinkComponent || BaseLinkComponent;

  if (textOnly) {
    if (isTruncated) {
      return (
        <Tooltip {...tooltipProps} tooltipContent={name}>
          <Truncate ref={ref} maxWidth={maxWidth} renderCustomContent={renderCustomContent}>
            {name}
          </Truncate>
        </Tooltip>
      );
    }
    return (
      <Truncate ref={ref} maxWidth={maxWidth} renderCustomContent={renderCustomContent}>
        {name}
      </Truncate>
    );
  } else if (isTruncated) {
    return (
      <Tooltip {...tooltipProps} tooltipContent={name}>
        <Link href={absoluteLink} rel="noopener noreferrer" {...linkProps}>
          <Truncate ref={ref} maxWidth={maxWidth} renderCustomContent={renderCustomContent}>
            {name}
          </Truncate>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Link href={absoluteLink} rel="noopener noreferrer" {...linkProps}>
      <Truncate ref={ref} maxWidth={maxWidth} renderCustomContent={renderCustomContent}>
        {name}
      </Truncate>
    </Link>
  );
};
